import * as React from 'react';
import {
    ContextActionType,
    ModalContext,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { ColumnDatatable } from '@europrocurement/flexy-datatable';
import {
    OffresJsonldOffreRead,
    TblDefDomaineJsonldOffreRead,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useDispatch } from 'react-redux';
import { FaOptionIcon, addItem } from '@europrocurement/l2d-icons';
import { AnyAction } from '@reduxjs/toolkit';
import { IconName } from '@fortawesome/pro-duotone-svg-icons';
import { visibilityTranslation } from '@europrocurement/l2d-utils';
import { useOffreService } from '@europrocurement/l2d-domain';
import { useDefaultContextActions } from '@europrocurement/l2d-modules/hooks';
import { Typography } from '@mui/material';
import OfferForm from './Form';
import ListingWithSearchAndPreview from '../components/ListingWithSearchAndPreview';
import ModalFormTitle from '../components/form/ModalFormTitle';
import { displayCustomValue, getStringColumn } from '../listings';
import { PossibleTypes } from '../../Societes';

const Panel: React.FunctionComponent = function () {
    const { modalActions } = React.useContext(ModalContext);

    const { offerModel: model } = useOffreService();
    const { backContextActions } = useDefaultContextActions();

    const dispatch = useDispatch();

    const reloadList = () =>
        dispatch(model.datasourcesThunks.main.getData({}) as unknown as AnyAction);

    const listingButton = {
        label: 'Créer une nouvelle offre',
        icon: addItem,
        action: () =>
            modalActions.call(
                <>
                    <ModalFormTitle
                        icon={model.icon}
                        title="Création d'une offre"
                    />
                    <OfferForm
                        isCreate
                        afterSubmit={reloadList}
                    />
                </>,
            ),
    };

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer une nouvelle offre',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: listingButton.action,
            },
            ...backContextActions,
        ],
        [backContextActions, listingButton.action],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<OffresJsonldOffreRead>[] = [
        getStringColumn('id'),
        displayCustomValue(
            'visibilite',
            (visibility) => visibilityTranslation[visibility as 'FULL' | 'PARTIAL'],
            'Visibilité',
        ),
        getStringColumn('libelle', 'Libellé'),
        {
            label: 'Icône',
            displayLabel: true,
            render: ({ iconeName }) => {
                if (iconeName === null) return null;

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        <FaOptionIcon icon={['fad', iconeName as IconName]} />
                    </Typography>
                );
            },
            isDisplayed: true,
        },
        displayCustomValue(
            'domaine',
            (domaine: TblDefDomaineJsonldOffreRead) => domaine?.libelle ?? '---',
            'Domaine',
        ),
    ];

    return (
        <ListingWithSearchAndPreview
            listingTitle="Liste des offres"
            listingButton={listingButton}
            listingColumns={columns}
            filtersControl={false}
            form={OfferForm}
            model={model}
            searchBarType={PossibleTypes.Offer}
        />
    );
};

export default Panel;
